// app/src/features/share/useShareWiring.js
import { useActiveForecast } from "../../hooks/useActiveForecast.js";
import { useCopyLinks } from "../../hooks/useCopyLinks.js";
import { useCopyLinksGate } from "../../hooks/useCopyLinksGate.js";
import { useForecastShares } from "../../hooks/useForecastShares.js";
import { useMintedLinkStash } from "../../hooks/useMintedLinkStash.js";
import { useSayerCopyOwnership } from "../../hooks/useSayerCopyOwnership.js";
import { useSession } from "../../hooks/useSession.js";
import { useShareCandidates } from "../../hooks/useShareCandidates.js";
import { useShareDialogGate } from "../../hooks/useShareDialogGate.js";
import { selectCurrentLink } from "../../lib/copyLinks/currentLink.js";
import { recordUiEvent } from "../../lib/logRingBuffer";
import { canOfferSharing } from "../../lib/shareDialog.js";

/**
 * The share wiring: the copy-link gate and its two data hooks (the link list and the minted-URL
 * stash — both held shut by `COPY_LINKS_UI_ON_HOLD`), the share-dialog gate with its two owner-only
 * reads (the forecast's shares and the share candidates), the `mkManageAccess` factory the ACCESS
 * row and the forecast header call at render, THE current link, and the Sayer-copy ownership
 * (`updateNow` / `applySchedule` adopt a Sooth Labs copy first). Called where `AppInner` called
 * `useSayerCopyOwnership` — after `useReforecast` (it needs `reforecast`) and before
 * `useChatMessages`; the six gate hooks register here in their old relative order, moved down from
 * their old point (nothing between the two points reads their outputs — the PR's hook-order note).
 *
 * `COPY_LINKS_UI_ON_HOLD` stays `AppInner`'s constant (the hold is one flip in App.jsx, where
 * `app/scripts/e2e-ci.mjs` and the telemetry-intents spec point) and arrives here under its own
 * name. `copyLinksEnabled` and `orgSharingEnabled` are read from the session context, the active
 * forecast through `useActiveForecast()`; `reforecast`, `applySchedule` and `forecastsById` are the
 * resubmit's, the tracking settings' and the lists'.
 *
 * Everything returned is read by `AppInner` only as a `frameCtx` key — eleven under the names the
 * timeline's contract body, the frame, the Forecast Manager and the tracking-settings overlay read,
 * and eight for the two hosts (`ShareHost`, `SendCopyHost`). `copyLinksGateOpen`, `shareIsOwner`
 * and `shareForecastId` are internal.
 *
 * @param {object} deps  the bindings `AppInner` declares before this point (see the body)
 */
export function useShareWiring({
  COPY_LINKS_UI_ON_HOLD,
  reforecast,
  applySchedule,
  forecastsById,
}) {
  const { copyLinksEnabled, orgSharingEnabled } = useSession();
  const active = useActiveForecast();
  const {
    available: copyLinksGateOpen,
    sendOpen: sendCopyOpen,
    openSend: openSendCopy,
    closeSend: closeSendCopy,
    listExpanded: copyLinksListExpanded,
    toggleList: toggleCopyLinksList,
  } = useCopyLinksGate({ copyLinksEnabled });
  const copyLinksAvailable = copyLinksGateOpen && !COPY_LINKS_UI_ON_HOLD;
  // `forecastId` is the SERVER uuid (`rowToWire`'s `forecastId`), never `active.id`
  // — that is the client_id, and both are UUIDs, so a client_id passes the route's
  // format guard and matches nothing. NULL while the gate is shut, which makes the
  // hook skip the list read entirely: with the flag off every copy-link route 404s,
  // so a request per boot would be noise for every user in that deployment.
  const copyLinks = useCopyLinks({
    forecastId: copyLinksAvailable ? active?.forecastId || null : null,
  });
  // The URL a mint returned, kept per-forecast in sessionStorage so "I copied it,
  // then copied something else, then needed it again" is recoverable. Same
  // forecastId as the list above, deliberately — a stash keyed to a different
  // forecast than the rows would offer a URL for a link not on screen.
  const mintedStash = useMintedLinkStash({
    forecastId: copyLinksAvailable ? active?.forecastId || null : null,
  });

  // ── Forecast SHARING — org/team/individual ──
  // One gate: the `chatboard-org-sharing` flag. `isOpen` is derived from `available`, so
  // the flag flipping off closes an open dialog with no transition to observe.
  const {
    available: shareDialogAvailable,
    isOpen: shareDialogOpen,
    open: openShareDialog,
    close: closeShareDialog,
    target: shareTarget,
  } = useShareDialogGate({ orgSharingEnabled });
  // NULL while the gate is shut, the dialog is closed, or the viewer is not the owner: a recipient
  // can open the dialog, but both reads behind it are owner-only server-side, so firing them would
  // buy a guaranteed 403. The id is read off the OPEN REQUEST's target, not the focused forecast,
  // because the button lives in the tracking-settings panel, which opens for whichever card was
  // clicked and never changes what is on screen.
  const shareIsOwner = shareTarget?.isOwn !== false;
  const shareForecastId = shareDialogOpen && shareIsOwner ? shareTarget?.forecastId || null : null;
  const forecastShares = useForecastShares({ forecastId: shareForecastId });
  const shareCandidates = useShareCandidates({ enabled: shareDialogOpen && shareIsOwner });

  // `isOwn` is deliberately NOT part of this gate — a recipient gets the button too, and the
  // dialog tells them in a sentence that only the owner manages sharing (see canOfferSharing's
  // own doc).
  //
  // This is a HANDLER, not a button. The control is the ACCESS row inside TrackingSettings, so the
  // panel owns how it looks while this keeps the `canOfferSharing` gate and the
  // `ui.share.dialog_open` telemetry that has to fire on the click itself. It is `undefined` when
  // the gate is shut, so the panel renders no ACCESS row rather than a dead one.
  //
  // It is a FACTORY over the panel's forecast `fc`, not a read of `active`: `openShareDialog` takes
  // that forecast from its opener, so the gate, the telemetry and the dialog all name the same
  // `fc`.
  const mkManageAccess = (fc) =>
    canOfferSharing({
      shareDialogAvailable,
      forecastId: fc?.forecastId,
    })
      ? () => {
          // The action, not the outcome: a bundle that shows the dialog was
          // opened and never saved reads very differently from one where it was
          // never opened. `isShared` is the state at the moment of the click.
          recordUiEvent({
            event: "ui.share.dialog_open",
            forecastId: fc?.forecastId || null,
            wasShared: fc?.isShared === true,
          });
          openShareDialog(fc);
        }
      : undefined;
  // THE link this panel is about: the newest live row with a usable URL, falling
  // back to this tab's stash only where the server has none. One definition, in
  // `lib/copyLinks/currentLink.js` — it names the visible URL, the copy button, the
  // revoke button and the row glyph, and a second notion would let those disagree.
  const currentLink = selectCurrentLink(copyLinks.links, {
    copyId: mintedStash.copyId,
    url: mintedStash.url,
  });
  // Update now and schedule changes make a Sooth Labs copy the user's own first.
  const sayerCopy = useSayerCopyOwnership({
    active,
    reforecast,
    applySchedule,
    getForecast: (id) => forecastsById.get(id),
  });

  return {
    copyLinks,
    copyLinksAvailable,
    copyLinksListExpanded,
    currentLink,
    mintedStash,
    openSendCopy,
    toggleCopyLinksList,
    mkManageAccess,
    openShareDialog,
    sayerCopy,
    shareDialogAvailable,
    shareDialogOpen,
    closeShareDialog,
    shareTarget,
    forecastShares,
    shareCandidates,
    sendCopyOpen,
    closeSendCopy,
  };
}
